import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Button, Loading, Tag, Tile } from "@carbon/react";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import { useID } from "../../hooks";
import {
  useApproveUser,
  useSetUserActive,
  useUserById,
} from "../../hooks/useUsers";
import { useNotification, useUser } from "../../context";
import { UserRole, userCompanyTypeMap } from "../../types";
import { ABILITIES } from "../../utils";
import "./user.scss";

// Which activation confirmation is currently open, if any. `null` means neither dialog is
// showing — ConfirmDialog is a single instance whose copy/labels swap on this, rather than two
// separate dialog instances, since the two flows are mutually exclusive (a user is either active
// or not) and never need to be open at once.
type ActivationAction = "deactivate" | "reactivate" | null;

const User = () => {
  const { openNotification } = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = useID();
  // `authUser` is the logged-in caller, distinct from `user` below (the record being viewed) —
  // needed to enforce "never on your own record" without relying on the backend's rejection.
  const { user: authUser, canManagePeople, can } = useUser();

  const [activationAction, setActivationAction] =
    useState<ActivationAction>(null);

  const { data: user, isLoading: isLoadingUser, isError } = useUserById(id);

  // PUT /user/{id}/approve, not the general-purpose PUT /user/{id}: that endpoint no longer
  // accepts a role field (Task 3), so round-tripping the fetched user through it left this
  // action half-applying - it wrote userCompanyType but never actually approved anyone. Approval
  // is its own endpoint precisely because the destination state must be hardcoded server-side.
  const { mutate: approveUser, isPending: isApprovingUser } = useApproveUser(
    () => {
      openNotification(t("users.approveSuccess"));
      navigate("/users");
    },
    () => openNotification(t("newMaterialForm.error"))
  );

  const { mutate: setUserActive, isPending: isSettingActive } =
    useSetUserActive(
      () => {
        openNotification(
          activationAction === "deactivate"
            ? t("users.deactivateSuccess")
            : t("users.reactivateSuccess")
        );
        setActivationAction(null);
      },
      () => {
        openNotification(t("newMaterialForm.error"));
        setActivationAction(null);
      }
    );

  const handleApprove = () => approveUser(id);

  const handleConfirmActivation = () => {
    setUserActive({ userId: id, active: activationAction === "reactivate" });
  };

  // The backend bars a user from deactivating themselves — offering the control here would just
  // be a lie the UI tells until the request 400s. `canManagePeople` is the same viewer-authority
  // check Approve now uses below (see UserProvider.tsx) — one rule, not two expressions of it.
  const canManageActivation =
    canManagePeople && !!authUser && authUser.id !== id;

  return (
    <div>
      <Button
        as={Link}
        to="/users"
        kind="ghost"
        size="sm"
        className="user__back"
      >
        {t("users.backToList")}
      </Button>

      {isLoadingUser ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : isError || !user ? (
        <EmptyState title={t("users.notFound")} />
      ) : (
        <>
          <PageHeader
            title={user.fullName}
            action={
              <div className="user__actions">
                {/* `/users/:id/edit` requires MANAGE_USERS (buyer) / SELLER_MANAGE_USERS (seller), so a
                    view-only role (e.g. a Visualizer) is redirected away — an unconditional Edit
                    link here would bounce it, one more control that lies about what it does.
                    Same ability gate Users.tsx uses for "Add user". */}
                {(can(ABILITIES.MANAGE_USERS) ||
                  can(ABILITIES.SELLER_MANAGE_USERS)) && (
                  <Button as={Link} to={`/users/${id}/edit`} kind="tertiary">
                    {t("cta.edit")}
                  </Button>
                )}
                {/* Gated on the target's state AND the viewer's own authority — previously
                    only the former, so a Visualizer (explicitly required to have no Approve
                    control) or any other non-managing role saw this button on a pending
                    colleague and got an honest error on click rather than never seeing it at
                    all. `canManagePeople` mirrors the backend's UserMutationGuard, the same
                    check `canManageActivation` above already made. */}
                {user.role === UserRole.ADMIN_APPROVAL && canManagePeople && (
                  <Button onClick={handleApprove} disabled={isApprovingUser}>
                    {t("users.approve")}
                  </Button>
                )}
                {canManageActivation ? (
                  user.active ? (
                    <Button
                      kind="danger--tertiary"
                      disabled={isSettingActive}
                      onClick={() => setActivationAction("deactivate")}
                    >
                      {t("users.deactivate")}
                    </Button>
                  ) : (
                    <Button
                      kind="tertiary"
                      disabled={isSettingActive}
                      onClick={() => setActivationAction("reactivate")}
                    >
                      {t("users.reactivate")}
                    </Button>
                  )
                ) : null}
              </div>
            }
          />

          <Tile className="user__card">
          <dl className="user__details">
            <div className="user__detail-row">
              <dt>{t("profile.email")}</dt>
              <dd>{user.email}</dd>
            </div>
            <div className="user__detail-row">
              <dt>{t("profile.phone")}</dt>
              <dd>{user.phone}</dd>
            </div>
            <div className="user__detail-row">
              <dt>{t("users.role")}</dt>
              <dd>
                {user.role === UserRole.BLOCKED
                  ? t("roles.blocked")
                  : userCompanyTypeMap[
                      user.userCompanyType as keyof typeof userCompanyTypeMap
                    ]}
              </dd>
            </div>
            {user.parent ? (
              <div className="user__detail-row">
                <dt>{t("users.responsible")}</dt>
                <dd>{user.parent.fullName}</dd>
              </div>
            ) : null}
            <div className="user__detail-row">
              <dt>{t("users.status")}</dt>
              <dd>
                {/* Status as text, not colour alone: the Tag's own label ("ACTIVE"/"INACTIVE")
                    carries the meaning, the colour is a reinforcing cue on top of it. */}
                <Tag type={user.active ? "green" : "gray"} size="sm">
                  {user.active ? t("cta.active") : t("cta.inactive")}
                </Tag>
              </dd>
            </div>
          </dl>
          </Tile>

          <ConfirmDialog
            open={activationAction !== null}
            danger={activationAction === "deactivate"}
            title={
              activationAction === "deactivate"
                ? t("users.deactivateTitle")
                : t("users.reactivateTitle")
            }
            body={
              activationAction === "deactivate"
                ? t("users.deactivateBody", { name: user.fullName })
                : t("users.reactivateBody", { name: user.fullName })
            }
            confirmLabel={
              activationAction === "deactivate"
                ? t("users.deactivate")
                : t("users.reactivate")
            }
            onConfirm={handleConfirmActivation}
            onCancel={() => setActivationAction(null)}
            isPending={isSettingActive}
          />
        </>
      )}
    </div>
  );
};

export default User;
